import { useLanguage } from '@/contexts/LanguageContext';

export default function SolutionsProcess() {
  const { t } = useLanguage();

  const steps = [
    {
      step: '01',
      icon: 'ri-file-search-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('需求盤點', 'Needs Assessment', 'ニーズ評価'),
      desc: t(
        '了解客戶現有原料來源、廢棄塑膠種類、產線設備與目標應用場景。',
        'Understand the client\'s existing raw material sources, waste plastic types, production line equipment, and target application scenarios.',
        'クライアントの既存原料源・廃プラスチック種類・生産ライン設備・目標応用シーンを把握します。'
      ),
    },
    {
      step: '02',
      icon: 'ri-microscope-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      title: t('原料與應用條件評估', 'Raw Material & Application Evaluation', '原料・応用条件評価'),
      desc: t(
        '分析原料物性、加工條件與目標規格，評估技術可行性與導入路徑。',
        'Analyze raw material properties, processing conditions, and target specifications to evaluate technical feasibility and implementation pathways.',
        '原料物性・加工条件・目標仕様を分析し、技術的実現可能性と導入経路を評価します。'
      ),
    },
    {
      step: '03',
      icon: 'ri-flask-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('PQernel / PQMB / rvPlastics 方案建議', 'PQernel / PQMB / rvPlastics Solution Recommendation', 'PQernel / PQMB / rvPlastics ソリューション提案'),
      desc: t(
        '依評估結果，提供最適合的材料組合與添加配方建議。',
        'Based on evaluation results, provide the most suitable material combination and additive formulation recommendations.',
        '評価結果に基づき、最適な材料組み合わせと添加配合の提案を行います。'
      ),
      highlight: true,
    },
    {
      step: '04',
      icon: 'ri-test-tube-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      title: t('小量試產', 'Small-Scale Pilot Production', '小規模試験生産'),
      desc: t(
        '協助安排小批量試產，驗證配方在實際產線上的可行性與穩定性。',
        'Assist in arranging small-batch pilot production to verify the feasibility and stability of the formulation on actual production lines.',
        '小ロット試験生産を手配し、実際の生産ラインでの配合の実現可能性と安定性を検証します。'
      ),
    },
    {
      step: '05',
      icon: 'ri-bar-chart-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('物性測試', 'Physical Property Testing', '物性試験'),
      desc: t(
        '進行拉伸強度、衝擊性、熔融指數等物性測試，確認達到目標規格。',
        'Conduct tensile strength, impact resistance, melt flow index, and other physical property tests to confirm target specifications are met.',
        '引張強度・衝撃性・メルトフローインデックスなどの物性試験を実施し、目標仕様の達成を確認します。'
      ),
    },
    {
      step: '06',
      icon: 'ri-settings-3-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      title: t('量產導入', 'Full-Scale Production Implementation', '量産導入'),
      desc: t(
        '確認物性達標後，協助客戶完成量產導入，提供持續技術支援。',
        'After confirming properties meet specifications, assist clients in completing full-scale production implementation with ongoing technical support.',
        '物性が仕様を満たすことを確認後、量産導入を完了し、継続的な技術サポートを提供します。'
      ),
    },
    {
      step: '07',
      icon: 'ri-leaf-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('ESG 與碳效益整理', 'ESG & Carbon Benefit Documentation', 'ESG・炭素効果の整理'),
      desc: t(
        '量化碳減排效益，整理 ESG 報告數據，支援品牌低碳產品認證申請。',
        'Quantify carbon reduction benefits, compile ESG report data, and support brand low-carbon product certification applications.',
        '炭素削減効果を定量化し、ESGレポートデータを整理し、ブランドの低炭素製品認証申請を支援します。'
      ),
    },
  ];

  return (
    <section className="section-padding bg-white">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14">
          <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">
            {t('導入流程', 'Implementation Process', '導入プロセス')}
          </p>
          <h2
            className="font-bold text-neutral-dark"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}
          >
            {t('聚量解決方案導入流程', 'PolyQ Solution Implementation Process', '聚量ソリューション導入プロセス')}
          </h2>
        </div>

        {/* Desktop: snake flow */}
        <div className="hidden lg:block">
          <div className="flex items-stretch gap-0 mb-4">
            {steps.slice(0, 4).map((s, idx) => (
              <div key={s.step} className="flex items-start flex-1">
                <div className="flex flex-col flex-1">
                  <div
                    className={`rounded-card border p-5 h-full ${s.highlight ? 'bg-brand-green border-brand-green/40 ring-2 ring-brand-green/20' : 'bg-white border-neutral-border'}`}
                    style={{ boxShadow: s.highlight ? '0 6px 24px rgba(0,184,148,0.18)' : '0 4px 16px rgba(0,0,0,0.06)' }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.highlight ? 'bg-white/20' : s.iconBg}`}>
                        <i className={`${s.icon} text-lg ${s.highlight ? 'text-white' : s.iconColor}`}></i>
                      </div>
                      <span className={`text-[0.75rem] font-bold tracking-widest ${s.highlight ? 'text-white/70' : 'text-neutral-mid'}`}>STEP {s.step}</span>
                    </div>
                    <p className={`font-semibold mb-1.5 text-[1rem] leading-snug ${s.highlight ? 'text-white' : 'text-neutral-dark'}`}>{s.title}</p>
                    <p className={`text-[0.9375rem] leading-relaxed ${s.highlight ? 'text-white/85' : 'text-neutral-mid'}`}>{s.desc}</p>
                    {s.highlight && (
                      <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 border border-white/30">
                        <i className="ri-star-line text-white text-xs"></i>
                        <span className="text-white text-[0.8125rem] font-semibold whitespace-nowrap">
                          {t('核心方案環節', 'Core Solution Step', 'コアソリューションステップ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {idx < 3 && (
                  <div className="flex items-center justify-center w-7 flex-shrink-0 pt-8">
                    <i className="ri-arrow-right-line text-brand-green text-lg"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end pr-[calc(25%-0.875rem)] mb-4">
            <i className="ri-arrow-down-line text-brand-green text-lg"></i>
          </div>
          <div className="flex items-stretch gap-0 flex-row-reverse">
            {steps.slice(4).map((s, idx) => (
              <div key={s.step} className="flex items-start flex-1">
                <div className="flex flex-col flex-1">
                  <div className="rounded-card border bg-white border-neutral-border p-5 h-full" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${s.iconBg} flex-shrink-0`}>
                        <i className={`${s.icon} ${s.iconColor} text-lg`}></i>
                      </div>
                      <span className="text-[0.75rem] font-bold tracking-widest text-neutral-mid">STEP {s.step}</span>
                    </div>
                    <p className="font-semibold text-neutral-dark mb-1.5 text-[1rem] leading-snug">{s.title}</p>
                    <p className="text-neutral-mid text-[0.9375rem] leading-relaxed">{s.desc}</p>
                  </div>
                </div>
                {idx < 2 && (
                  <div className="flex items-center justify-center w-7 flex-shrink-0 pt-8">
                    <i className="ri-arrow-left-line text-brand-green text-lg"></i>
                  </div>
                )}
              </div>
            ))}
            <div className="flex-1" />
          </div>
        </div>

        {/* Mobile: vertical */}
        <div className="lg:hidden flex flex-col gap-3">
          {steps.map((s, idx) => (
            <div key={s.step} className="flex flex-col items-center">
              <div
                className={`w-full rounded-card border p-5 ${s.highlight ? 'bg-brand-green border-brand-green/40' : 'bg-white border-neutral-border'}`}
                style={{ boxShadow: s.highlight ? '0 6px 24px rgba(0,184,148,0.18)' : '0 4px 16px rgba(0,0,0,0.06)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.highlight ? 'bg-white/20' : s.iconBg}`}>
                    <i className={`${s.icon} text-lg ${s.highlight ? 'text-white' : s.iconColor}`}></i>
                  </div>
                  <span className={`text-[0.75rem] font-bold tracking-widest ${s.highlight ? 'text-white/70' : 'text-neutral-mid'}`}>STEP {s.step}</span>
                </div>
                <p className={`font-semibold mb-1 text-[1.0625rem] ${s.highlight ? 'text-white' : 'text-neutral-dark'}`}>{s.title}</p>
                <p className={`text-[1rem] leading-relaxed ${s.highlight ? 'text-white/85' : 'text-neutral-mid'}`}>{s.desc}</p>
              </div>
              {idx < steps.length - 1 && (
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-arrow-down-line text-brand-green text-lg"></i>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
